import { shouldSkipObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import type { Projector } from './projector';

type OverlayUserData = {
  projectorOverlayIgnore?: boolean;
  projectorOverlayState?: {
    transparent: boolean;
    depthWrite: boolean;
    depthTest: boolean;
    blending: THREE.Blending;
    premultipliedAlpha: boolean;
  };
  projectorFrustumOverlay?: {
    applied?: boolean;
    uniforms?: THREE.Shader['uniforms'];
  };
};

export type ProjectorFrustumOverlayOptions = {
  maxProjectors?: number;
  fillColor?: THREE.ColorRepresentation;
  fillOpacity?: number;
  depthSize?: number;
  depthBias?: number;
  facingThreshold?: number;
  useTextureArray?: boolean;
  luxSpectrum?: boolean;
  luxMin?: number;
  luxMax?: number;
  luxReferenceDistance?: number;
  luxColors?: THREE.ColorRepresentation[];
  luxLogScale?: boolean;
};

export class ProjectorFrustumOverlay {
  private readonly maxProjectors: number;
  private readonly fillColor: THREE.Color;
  private fillOpacity: number;
  private readonly materials = new Set<THREE.Material>();
  private readonly matrices: THREE.Matrix4[];
  private readonly projectorPositions: THREE.Vector3[];
  private readonly projectorLuxFactors: number[];
  private useLuxSpectrum: boolean;
  private luxMin: number;
  private luxMax: number;
  private luxReferenceDistance: number;
  private readonly luxColors: THREE.Color[];
  private luxLogScale: boolean;
  private computedLuxMax: number;
  private sceneLuxMin: number;
  private sceneLuxMax: number;
  private depthTargets: THREE.WebGLRenderTarget[] = [];
  private depthTextures: THREE.DepthTexture[] = [];
  private readonly depthArrayTarget?: THREE.WebGLArrayRenderTarget;
  private readonly depthMaterial: THREE.MeshDepthMaterial;
  private readonly depthSize: number;
  private readonly depthBias: number;
  private readonly useTextureArray: boolean;
  private facingThreshold: number;
  private overlayOnly = false;

  constructor(options: ProjectorFrustumOverlayOptions = {}) {
    this.maxProjectors = Math.max(1, options.maxProjectors ?? 16);
    this.fillColor = new THREE.Color(options.fillColor ?? 0x2aa6ff);
    this.fillOpacity = options.fillOpacity ?? 0.12;
    this.depthSize = options.depthSize ?? 1024;
    this.depthBias = options.depthBias ?? 0.0025;
    this.facingThreshold = options.facingThreshold ?? -1;
    this.useTextureArray = options.useTextureArray ?? false;
    this.useLuxSpectrum = options.luxSpectrum ?? false;
    this.luxMin = options.luxMin ?? 0;
    this.luxMax = options.luxMax ?? 0;
    this.luxReferenceDistance = options.luxReferenceDistance ?? 1.0;
    this.luxLogScale = options.luxLogScale ?? false;
    this.matrices = Array.from({ length: this.maxProjectors }, () => new THREE.Matrix4());
    this.projectorPositions = Array.from({ length: this.maxProjectors }, () => new THREE.Vector3());
    this.projectorLuxFactors = Array.from({ length: this.maxProjectors }, () => 0);
    const defaultLuxColors: THREE.ColorRepresentation[] = [0x2b46ff, 0x2aa6ff, 0x39d98a, 0xffd66b, 0xff5a6a];
    const requestedColors = options.luxColors && options.luxColors.length > 0 ? options.luxColors : defaultLuxColors;
    this.luxColors = Array.from({ length: 5 }, (_, index) => {
      const color = requestedColors[Math.min(index, requestedColors.length - 1)];
      return new THREE.Color(color);
    });
    this.computedLuxMax = this.luxMax;
    this.sceneLuxMin = 0;
    this.sceneLuxMax = 0;
    this.depthMaterial = new THREE.MeshDepthMaterial();
    this.depthMaterial.blending = THREE.NoBlending;

    if (this.useTextureArray) {
      this.depthMaterial.depthPacking = THREE.RGBADepthPacking;
      const target = new THREE.WebGLArrayRenderTarget(this.depthSize, this.depthSize, this.maxProjectors, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        depthBuffer: true,
        stencilBuffer: false,
      });
      target.texture.minFilter = THREE.NearestFilter;
      target.texture.magFilter = THREE.NearestFilter;
      target.texture.generateMipmaps = false;
      target.texture.wrapS = THREE.ClampToEdgeWrapping;
      target.texture.wrapT = THREE.ClampToEdgeWrapping;
      this.depthArrayTarget = target;
    } else {
      this.depthTargets = Array.from({ length: this.maxProjectors }, () => {
        const target = new THREE.WebGLRenderTarget(this.depthSize, this.depthSize);
        target.texture.minFilter = THREE.NearestFilter;
        target.texture.magFilter = THREE.NearestFilter;
        target.texture.generateMipmaps = false;
        target.depthTexture = new THREE.DepthTexture(this.depthSize, this.depthSize);
        target.depthTexture.format = THREE.DepthFormat;
        target.depthTexture.type = THREE.UnsignedIntType;
        target.depthTexture.minFilter = THREE.NearestFilter;
        target.depthTexture.magFilter = THREE.NearestFilter;
        return target;
      });
      this.depthTextures = this.depthTargets.map((target) => target.depthTexture);
    }
  }

  applyTo(root: THREE.Object3D) {
    root.traverse((object) => {
      if (!('isMesh' in object) || !(object as THREE.Mesh).isMesh) {
        return;
      }

      const mesh = object as THREE.Mesh;
      if ((mesh.userData as OverlayUserData).projectorOverlayIgnore) {
        return;
      }

      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material) => this.decorateMaterial(material));
      } else if (mesh.material) {
        this.decorateMaterial(mesh.material);
      }
    });
  }

  update(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    projectors: Projector[],
    options: { overlayOnly?: boolean } = {}
  ) {
    const count = Math.min(projectors.length, this.maxProjectors);
    this.overlayOnly = options.overlayOnly ?? false;
    if (count === 0) {
      this.applyUniforms(0);
      return;
    }

    const previousRenderTarget = renderer.getRenderTarget();
    const previousClearColor = new THREE.Color();
    renderer.getClearColor(previousClearColor);
    const previousClearAlpha = renderer.getClearAlpha();
    const previousAutoClear = renderer.autoClear;
    const previousOverrideMaterial = scene.overrideMaterial;

    renderer.autoClear = true;
    scene.overrideMaterial = this.depthMaterial;
    renderer.setClearColor(0x000000, 1);

    const hiddenObjects: THREE.Object3D[] = [];
    scene.traverse((object: THREE.Object3D) => {
      if (object.visible && shouldSkipObject(object)) {
        object.visible = false;
        hiddenObjects.push(object);
      }
    });

    for (let i = 0; i < count; i += 1) {
      const projector = projectors[i];
      projector.updateMatrixWorld(true);
      projector.camera.updateMatrixWorld(true);
      this.matrices[i].multiplyMatrices(projector.camera.projectionMatrix, projector.camera.matrixWorldInverse);
      this.projectorPositions[i].setFromMatrixPosition(projector.camera.matrixWorld);
      this.projectorLuxFactors[i] = this.getLuxFactor(projector);

      if (this.useTextureArray) {
        if (!this.depthArrayTarget) {
          continue;
        }
        renderer.setRenderTarget(this.depthArrayTarget, i);
      } else {
        renderer.setRenderTarget(this.depthTargets[i]);
      }
      renderer.clear();
      renderer.render(scene, projector.camera);
    }

    for (const object of hiddenObjects) {
      object.visible = true;
    }

    renderer.setRenderTarget(previousRenderTarget);
    renderer.setClearColor(previousClearColor, previousClearAlpha);
    renderer.autoClear = previousAutoClear;
    scene.overrideMaterial = previousOverrideMaterial;

    this.applyUniforms(count);
  }

  setFillOpacity(opacity: number) {
    this.fillOpacity = Math.min(1, Math.max(0, opacity));
  }

  setFacingThreshold(threshold: number) {
    this.facingThreshold = Math.max(-1, Math.min(1, threshold));
  }

  clearOverlay() {
    this.overlayOnly = false;
    this.applyUniforms(0);
  }

  getBatchSize() {
    return this.maxProjectors;
  }

  beginOverlayPass() {
    for (const material of this.materials) {
      const userData = material.userData as OverlayUserData;
      if (!userData.projectorOverlayState) {
        userData.projectorOverlayState = {
          transparent: material.transparent,
          depthWrite: material.depthWrite,
          depthTest: material.depthTest,
          blending: material.blending,
          premultipliedAlpha: material.premultipliedAlpha,
        };
      }

      material.transparent = false;
      material.depthWrite = true;
      material.depthTest = true;
      material.blending = THREE.NormalBlending;
      material.premultipliedAlpha = false;
    }
  }

  endOverlayPass() {
    for (const material of this.materials) {
      const userData = material.userData as OverlayUserData;
      const state = userData.projectorOverlayState;
      if (!state) {
        continue;
      }

      material.transparent = state.transparent;
      material.depthWrite = state.depthWrite;
      material.depthTest = state.depthTest;
      material.blending = state.blending;
      material.premultipliedAlpha = state.premultipliedAlpha;
    }
  }

  private decorateMaterial(material: THREE.Material) {
    if (!('isMeshStandardMaterial' in material) && !('isMeshPhysicalMaterial' in material)) {
      return;
    }

    const userData = material.userData as OverlayUserData;
    if (userData.projectorFrustumOverlay?.applied) {
      return;
    }

    const previous = material.onBeforeCompile;
    material.onBeforeCompile = (shader) => {
      previous?.(shader);

      shader.uniforms.uProjectorCount = { value: 0 };
      shader.uniforms.uProjectorMatrix = {
        value: this.matrices.map((m) => m.clone()),
      };
      shader.uniforms.uProjectorFillColor = { value: this.fillColor };
      shader.uniforms.uProjectorFillOpacity = { value: this.fillOpacity };
      shader.uniforms.uProjectorPosition = {
        value: this.projectorPositions.map((p) => p.clone()),
      };
      shader.uniforms.uProjectorLuxFactor = {
        value: this.projectorLuxFactors.slice(),
      };
      shader.uniforms.uProjectorUseLuxSpectrum = {
        value: this.useLuxSpectrum ? 1 : 0,
      };
      shader.uniforms.uProjectorLuxMin = { value: this.luxMin };
      shader.uniforms.uProjectorLuxMax = { value: this.computedLuxMax };
      shader.uniforms.uProjectorLuxLog = { value: this.luxLogScale ? 1 : 0 };
      shader.uniforms.uProjectorLuxColor0 = { value: this.luxColors[0] };
      shader.uniforms.uProjectorLuxColor1 = { value: this.luxColors[1] };
      shader.uniforms.uProjectorLuxColor2 = { value: this.luxColors[2] };
      shader.uniforms.uProjectorLuxColor3 = { value: this.luxColors[3] };
      shader.uniforms.uProjectorLuxColor4 = { value: this.luxColors[4] };
      shader.uniforms.uProjectorOverlayOnly = { value: 0 };
      shader.uniforms.uProjectorBias = { value: this.depthBias };
      shader.uniforms.uProjectorFacingThreshold = {
        value: this.facingThreshold,
      };
      if (this.useTextureArray) {
        shader.uniforms.uProjectorDepthArray = {
          value: this.depthArrayTarget?.texture ?? null,
        };
      } else {
        for (let i = 0; i < this.depthTextures.length; i += 1) {
          shader.uniforms[`uProjectorDepth${i}`] = {
            value: this.depthTextures[i],
          };
        }
      }

      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n' + 'varying vec3 vProjectorWorldPosition;')
        .replace(
          '#include <project_vertex>',
          [
            '#include <project_vertex>',
            'vec4 projectorWorldPosition = vec4( transformed, 1.0 );',
            '#ifdef USE_BATCHING',
            '  projectorWorldPosition = batchingMatrix * projectorWorldPosition;',
            '#endif',
            '#ifdef USE_INSTANCING',
            '  projectorWorldPosition = instanceMatrix * projectorWorldPosition;',
            '#endif',
            'projectorWorldPosition = modelMatrix * projectorWorldPosition;',
            'vProjectorWorldPosition = projectorWorldPosition.xyz;',
          ].join('\n')
        );

      const fragmentHeaderLines = [
        '#include <common>',
        `#define MAX_PROJECTORS ${this.maxProjectors}`,
        'varying vec3 vProjectorWorldPosition;',
        'uniform int uProjectorCount;',
        'uniform mat4 uProjectorMatrix[MAX_PROJECTORS];',
        'uniform vec3 uProjectorFillColor;',
        'uniform float uProjectorFillOpacity;',
        'uniform vec3 uProjectorPosition[MAX_PROJECTORS];',
        'uniform float uProjectorLuxFactor[MAX_PROJECTORS];',
        'uniform float uProjectorUseLuxSpectrum;',
        'uniform float uProjectorLuxMin;',
        'uniform float uProjectorLuxMax;',
        'uniform float uProjectorLuxLog;',
        'uniform vec3 uProjectorLuxColor0;',
        'uniform vec3 uProjectorLuxColor1;',
        'uniform vec3 uProjectorLuxColor2;',
        'uniform vec3 uProjectorLuxColor3;',
        'uniform vec3 uProjectorLuxColor4;',
        'uniform float uProjectorOverlayOnly;',
        'uniform float uProjectorBias;',
        'uniform float uProjectorFacingThreshold;',
      ];

      if (this.useTextureArray) {
        fragmentHeaderLines.push('uniform sampler2DArray uProjectorDepthArray;');
      } else {
        fragmentHeaderLines.push(
          ...Array.from({ length: this.maxProjectors }, (_, i) => `uniform sampler2D uProjectorDepth${i};`)
        );
      }

      if (this.useTextureArray) {
        fragmentHeaderLines.push(
          'float projectorOverlayUnpackRGBAToDepth(const in vec4 v) {',
          '  const vec4 UnpackFactors4 = vec4(',
          '    255.0 / 256.0,',
          '    (255.0 / 256.0) / 256.0,',
          '    (255.0 / 256.0) / (256.0 * 256.0),',
          '    1.0 / (256.0 * 256.0 * 256.0)',
          '  );',
          '  return dot(v, UnpackFactors4);',
          '}'
        );
      }

      fragmentHeaderLines.push(
        'vec3 projectorLuxRamp(float t) {',
        '  t = clamp(t, 0.0, 1.0);',
        '  if (t < 0.25) {',
        '    return mix(uProjectorLuxColor0, uProjectorLuxColor1, t / 0.25);',
        '  }',
        '  if (t < 0.5) {',
        '    return mix(uProjectorLuxColor1, uProjectorLuxColor2, (t - 0.25) / 0.25);',
        '  }',
        '  if (t < 0.75) {',
        '    return mix(uProjectorLuxColor2, uProjectorLuxColor3, (t - 0.5) / 0.25);',
        '  }',
        '  return mix(uProjectorLuxColor3, uProjectorLuxColor4, (t - 0.75) / 0.25);',
        '}'
      );

      const fragmentHeader = fragmentHeaderLines.join('\n');

      let overlayBlocks = '';
      if (this.useTextureArray) {
        overlayBlocks = [
          'float projectorFillCount = 0.0;',
          'float projectorLux = 0.0;',
          'vec3 viewPos = -vViewPosition;',
          'for (int i = 0; i < MAX_PROJECTORS; i++) {',
          '  if (i >= uProjectorCount) { break; }',
          '  vec4 clip = uProjectorMatrix[i] * vec4(vProjectorWorldPosition, 1.0);',
          '  if (clip.w <= 0.0) { continue; }',
          '  vec3 ndc = clip.xyz / clip.w;',
          '  if (ndc.x < -1.0 || ndc.x > 1.0 || ndc.y < -1.0 || ndc.y > 1.0 || ndc.z < -1.0 || ndc.z > 1.0) { continue; }',
          '  vec2 uv = ndc.xy * 0.5 + 0.5;',
          '  float depthSample = projectorOverlayUnpackRGBAToDepth(texture(uProjectorDepthArray, vec3(uv, float(i))));',
          '  float fragDepth = ndc.z * 0.5 + 0.5;',
          '  if (fragDepth - uProjectorBias > depthSample) { continue; }',
          '  if (uProjectorFacingThreshold > -0.999) {',
          '    vec3 projectorViewPos = (viewMatrix * vec4(uProjectorPosition[i], 1.0)).xyz;',
          '    vec3 projectorDir = normalize(projectorViewPos - viewPos);',
          '    if (dot(normal, projectorDir) <= uProjectorFacingThreshold) { continue; }',
          '  }',
          '  projectorFillCount += 1.0;',
          '  if (uProjectorUseLuxSpectrum > 0.5) {',
          '    float distanceToProjector = length(vProjectorWorldPosition - uProjectorPosition[i]);',
          '    float lux = uProjectorLuxFactor[i] / max(distanceToProjector * distanceToProjector, 1e-4);',
          '    projectorLux += lux;',
          '  }',
          '}',
          'float projectorFill = step(0.5, projectorFillCount);',
          'float fillAlpha = projectorFill * uProjectorFillOpacity;',
          'vec3 fillColor = uProjectorFillColor;',
          'if (uProjectorUseLuxSpectrum > 0.5) {',
          '  float luxT;',
          '  if (uProjectorLuxLog > 0.5) {',
          '    float minLux = max(uProjectorLuxMin, 1e-4);',
          '    float maxLux = max(uProjectorLuxMax, minLux + 1e-4);',
          '    float luxValue = max(projectorLux, minLux);',
          '    luxT = (log(luxValue) - log(minLux)) / max(log(maxLux) - log(minLux), 1e-4);',
          '  } else {',
          '    float luxRange = max(uProjectorLuxMax - uProjectorLuxMin, 1e-4);',
          '    luxT = (projectorLux - uProjectorLuxMin) / luxRange;',
          '  }',
          '  luxT = clamp(luxT, 0.0, 1.0);',
          '  fillColor = projectorLuxRamp(luxT);',
          '}',
          'if (uProjectorOverlayOnly > 0.5) {',
          '  // Overlay pass mode: add overlay to existing material color',
          '  outgoingLight = outgoingLight + fillColor * fillAlpha;',
          '} else {',
          '  outgoingLight = mix(outgoingLight, fillColor, fillAlpha);',
          '}',
        ].join('\n');
      } else {
        overlayBlocks = [
          'float projectorFillCount = 0.0;',
          'float projectorLux = 0.0;',
          'vec3 viewPos = -vViewPosition;',
          ...Array.from({ length: this.maxProjectors }, (_, i) => {
            return [
              `if (uProjectorCount > ${i}) {`,
              `  vec4 clip${i} = uProjectorMatrix[${i}] * vec4(vProjectorWorldPosition, 1.0);`,
              `  if (clip${i}.w > 0.0) {`,
              `    vec3 ndc${i} = clip${i}.xyz / clip${i}.w;`,
              `    if (ndc${i}.x >= -1.0 && ndc${i}.x <= 1.0 && ndc${i}.y >= -1.0 && ndc${i}.y <= 1.0 && ndc${i}.z >= -1.0 && ndc${i}.z <= 1.0) {`,
              `      vec2 uv${i} = ndc${i}.xy * 0.5 + 0.5;`,
              `      float depthSample${i} = texture2D(uProjectorDepth${i}, uv${i}).x;`,
              `      float fragDepth${i} = ndc${i}.z * 0.5 + 0.5;`,
              `      if (fragDepth${i} - uProjectorBias <= depthSample${i}) {`,
              `        float facing${i} = dot(normal, normalize((viewMatrix * vec4(uProjectorPosition[${i}], 1.0)).xyz - viewPos));`,
              `        if (uProjectorFacingThreshold <= -0.999 || facing${i} > uProjectorFacingThreshold) {`,
              `          projectorFillCount += 1.0;`,
              `          if (uProjectorUseLuxSpectrum > 0.5) {`,
              `            float distanceToProjector${i} = length(vProjectorWorldPosition - uProjectorPosition[${i}]);`,
              `            float lux${i} = uProjectorLuxFactor[${i}] / max(distanceToProjector${i} * distanceToProjector${i}, 1e-4);`,
              `            projectorLux += lux${i};`,
              `          }`,
              '        }',
              '      }',
              '    }',
              '  }',
              '}',
            ].join('\n');
          }),
          'float projectorFill = step(0.5, projectorFillCount);',
          'float fillAlpha = projectorFill * uProjectorFillOpacity;',
          'vec3 fillColor = uProjectorFillColor;',
          'if (uProjectorUseLuxSpectrum > 0.5) {',
          '  float luxT;',
          '  if (uProjectorLuxLog > 0.5) {',
          '    float minLux = max(uProjectorLuxMin, 1e-4);',
          '    float maxLux = max(uProjectorLuxMax, minLux + 1e-4);',
          '    float luxValue = max(projectorLux, minLux);',
          '    luxT = (log(luxValue) - log(minLux)) / max(log(maxLux) - log(minLux), 1e-4);',
          '  } else {',
          '    float luxRange = max(uProjectorLuxMax - uProjectorLuxMin, 1e-4);',
          '    luxT = (projectorLux - uProjectorLuxMin) / luxRange;',
          '  }',
          '  luxT = clamp(luxT, 0.0, 1.0);',
          '  fillColor = projectorLuxRamp(luxT);',
          '}',
          'if (uProjectorOverlayOnly > 0.5) {',
          '  // Overlay pass mode: add overlay to existing material color',
          '  outgoingLight = outgoingLight + fillColor * fillAlpha;',
          '} else {',
          '  outgoingLight = mix(outgoingLight, fillColor, fillAlpha);',
          '}',
        ].join('\n');
      }

      const withHeader = shader.fragmentShader.replace('#include <common>', fragmentHeader);
      let fragment = withHeader.replace(
        '#include <output_fragment>',
        [overlayBlocks, '#include <output_fragment>'].join('\n')
      );

      if (fragment === withHeader) {
        fragment = withHeader.replace(
          '#include <opaque_fragment>',
          [overlayBlocks, '#include <opaque_fragment>'].join('\n')
        );
      }

      shader.fragmentShader = fragment;

      userData.projectorFrustumOverlay = {
        applied: true,
        uniforms: shader.uniforms,
      };
    };

    material.needsUpdate = true;
    userData.projectorFrustumOverlay = { applied: true };
    this.materials.add(material);
  }

  private applyUniforms(count: number) {
    for (const material of this.materials) {
      const data = (material.userData as OverlayUserData).projectorFrustumOverlay;
      if (!data?.uniforms) {
        continue;
      }

      data.uniforms.uProjectorCount.value = count;
      data.uniforms.uProjectorFillColor.value = this.fillColor;
      data.uniforms.uProjectorFillOpacity.value = this.fillOpacity;
      data.uniforms.uProjectorUseLuxSpectrum.value = this.useLuxSpectrum ? 1 : 0;
      data.uniforms.uProjectorLuxMin.value = this.luxMin;
      data.uniforms.uProjectorLuxMax.value = this.computedLuxMax;
      data.uniforms.uProjectorLuxLog.value = this.luxLogScale ? 1 : 0;
      data.uniforms.uProjectorOverlayOnly.value = this.overlayOnly ? 1 : 0;
      data.uniforms.uProjectorBias.value = this.depthBias;
      data.uniforms.uProjectorFacingThreshold.value = this.facingThreshold;
      if (this.useTextureArray && data.uniforms.uProjectorDepthArray) {
        data.uniforms.uProjectorDepthArray.value = this.depthArrayTarget?.texture ?? null;
      }

      const target = data.uniforms.uProjectorMatrix.value as THREE.Matrix4[];
      for (let i = 0; i < count; i += 1) {
        target[i].copy(this.matrices[i]);
      }
      const positionTarget = data.uniforms.uProjectorPosition.value as THREE.Vector3[];
      const luxTarget = data.uniforms.uProjectorLuxFactor.value as number[];
      for (let i = 0; i < count; i += 1) {
        positionTarget[i].copy(this.projectorPositions[i]);
        luxTarget[i] = this.projectorLuxFactors[i];
      }
    }
  }

  setLuxSpectrum(enabled: boolean) {
    this.useLuxSpectrum = enabled;
  }

  setLuxLogScale(enabled: boolean) {
    this.luxLogScale = enabled;
  }

  setLuxReferenceDistance(distance: number) {
    this.luxReferenceDistance = Math.max(0.1, distance);
  }

  setLuxMax(maxLux: number) {
    this.luxMax = Math.max(0, maxLux);
  }

  setLuxMin(minLux: number) {
    this.luxMin = Math.max(0, minLux);
  }

  getSceneLuxStats() {
    return {
      min: this.sceneLuxMin,
      max: this.sceneLuxMax,
    };
  }

  updateLuxStats(projectors: Projector[]) {
    if (projectors.length === 0) {
      this.sceneLuxMin = 0;
      this.sceneLuxMax = 0;
      this.computedLuxMax = Math.max(this.luxMax, this.luxMin + 0.001);
      return;
    }

    const referenceDistance = Math.max(0.01, this.luxReferenceDistance);
    const referenceDistanceSq = referenceDistance * referenceDistance;
    let minLux = Number.POSITIVE_INFINITY;
    let maxLux = 0;
    for (const projector of projectors) {
      const lux = this.getLuxFactor(projector) / referenceDistanceSq;
      minLux = Math.min(minLux, lux);
      maxLux = Math.max(maxLux, lux);
    }
    this.sceneLuxMin = Number.isFinite(minLux) ? minLux : 0;
    this.sceneLuxMax = maxLux;
    if (this.luxMax > 0) {
      this.computedLuxMax = this.luxMax;
    } else {
      this.computedLuxMax = Math.max(maxLux, this.luxMin + 0.001);
    }
  }

  private getLuxFactor(projector: Projector) {
    const fovY = THREE.MathUtils.degToRad(projector.camera.fov);
    const tanY = Math.tan(fovY / 2);
    const tanX = tanY * projector.camera.aspect;
    const denominator = Math.max(4 * tanX * tanY, 1e-6);
    const lumens = Math.max(0, projector.userData?.lumens);
    const brightness = Math.max(0, projector.userData?.power_brightness / 100);
    const stacked = Math.max(1, (projector.userData as any)?.stackedProjectors ?? 1);
    const gain = Math.max(0, projector.userData?.gain ?? 0.5);
    return ((lumens * brightness * stacked * gain) / denominator) * 100;
  }
}
